/**
 * Core (jXchange) probe - POST v3/loans/admin/tools/jx-probe (action tools.jx-probe).
 * Read-only: the server allows Inq/Srch ops on the BFF allow-list only and redacts the answer before it leaves pcb-loans-api.
 * Replaces the LWP admin panel's jX probe, whose redaction ran in the browser (the server's is authoritative).
 */

import { useState } from 'react';
import { Banner, Button, Spinner } from '@/components/primitives';
import { SelectField, TextArea, TextField } from '@/components/FormField';
import { loansApi } from '../../loansApi';
import { errorText } from '../common';

const OPS = [
  'Customer/CustInq',
  'Customer/CustSrch',
  'Customer/AcctInq',
  'Customer/AcctSrch',
  'Customer/CustAcctRelInq',
  'Customer/CustRelInq',
  'Customer/AcctRelSrch',
  'Customer/ParmValSrch',
  'Inquiry/FileMainSrch',
  'Inquiry/EscrwInq',
  'Inquiry/AcctHistSrch',
];

const EXAMPLES: Record<string, string> = {
  'Customer/CustInq': '{ "CustId": "", "IncXtendElemArray": { "IncXtendElemInfo": [{ "XtendElem": "x_BusDetail" }] } }',
  'Customer/AcctInq': '{ "InAcctId": { "AcctId": "", "AcctType": "40" }, "IncXtendElemArray": [{ "IncXtendElemInfo": { "XtendElem": "x_LnInfoRec" } }] }',
  'Customer/ParmValSrch': '{ "ParmName": "CollatCode" }',
  'Inquiry/FileMainSrch': '{ "InAcctId": { "AcctId": "", "AcctType": "80" } }',
};

export function LoansJxProbePage(): JSX.Element {
  const [op, setOp] = useState(OPS[0]);
  const [body, setBody] = useState(EXAMPLES[OPS[0]] ?? '{}');
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  let parsed: unknown = undefined;
  let bodyError: string | undefined;
  try {
    parsed = body.trim() ? JSON.parse(body) : undefined;
  } catch (e) {
    bodyError = `Not valid JSON: ${e instanceof Error ? e.message : String(e)}`;
  }

  const run = async (): Promise<void> => {
    if (bodyError) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    try {
      // A read that has to be a POST. It changes nothing; the reason only labels the audit row (the server records op + caller).
      const r = await loansApi.send<{ result: unknown }>('POST', 'v3/loans/admin/tools/jx-probe', { op, body: parsed, query: query.trim() || undefined }, `Core probe ${op} (read-only)`);
      setAnswer(r.result);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>Core probe (jXchange)</h2>
      <p className="page-intro">
        Read-only inquiries against Core Director through pcb-loans-api. Only inquiry and search operations are allowed, and customer
        data in the answer is masked on the server. Each call is recorded with the operation and your account.
      </p>
      <div className="toolbar">
        <SelectField
          label="Operation"
          value={op}
          onChange={(v) => {
            setOp(v);
            if (EXAMPLES[v]) setBody(EXAMPLES[v]);
          }}
          options={OPS.map((o) => ({ value: o, label: o }))}
        />
        <TextField label="Query string (optional)" value={query} onChange={setQuery} placeholder="Count=500" monospace />
      </div>
      <TextArea label="Request body (JSON)" value={body} onChange={setBody} rows={8} error={bodyError} />
      <div className="card-actions-row">
        <Button onClick={() => void run()} disabled={!!bodyError || loading}>
          {loading ? 'Asking Core…' : 'Run'}
        </Button>
      </div>
      {error && <Banner tone="err">{error}</Banner>}
      {loading && <Spinner label="Asking Core…" />}
      {answer !== null && <pre style={{ maxHeight: 560, overflow: 'auto', fontSize: 12, background: 'var(--surface-2, #f6f7f9)', padding: 12, borderRadius: 6 }}>{JSON.stringify(answer, null, 2)}</pre>}
    </div>
  );
}
